<script setup>

defineProps({
  title: {
    type: String,
    default: () => '',
  },
  label: {
    type: String,
    default: () => '',
  },
  quantity: {
    type: [String, Number],
    default: () => 0,
  },
  brands: {
    type: Array,
    default: () => [],
  },
  services: {
    type: Array,
    default: () => [],
  },
});
</script>
<template>
  <div class="region" :class="{ 'region--witharrow': quantity > 0 }">
    <div class="region__top">
      <p v-if="title" class="mb-0 region__title h5">
        <span class="region__title-value" v-html="title"></span>
        <span class="region__quantity body-3-bold" v-html="quantity"></span>
      </p>
      <div v-if="quantity > 0" class="region__arrow">
        <CIcon
          class="region__arrow-icon button-next__icon"
          name="direction-black"
          width="16"
          height="16"
        />
      </div>
    </div>
    <div
      v-if="(brands && brands.length > 0) || (services && services.length > 0)"
      class="region__bottom"
    >
      <template v-if="brands && brands.length > 0">
        <p class="mb-0 region__bottom-title body-3-regular">
          {{ $t('networkMap.brands') }}
        </p>
        <ul class="m-0 p-0 region__brands">
          <li
            v-for="(brand, brandIndex) in brands"
            :key="brandIndex"
            v-tooltip.top="{
              value: brand.alt,
              class: 'region__brand-tooltip',
            }"
            class="m-0 region__brand"
          >
            <NuxtImg
              v-if="brand?.src"
              :src="brand.src"
              :alt="brand.alt"
              :height="brand.height"
              :width="brand.width"
              class="region__brand-image"
              loading="lazy"
            />
          </li>
        </ul>
      </template>
      <template v-if="services && services.length > 0">
        <p class="mb-0 region__bottom-title body-3-regular">
          {{ $t('networkMap.services') }}
        </p>
        <ul class="m-0 p-0 region__services">
          <li
            v-for="(service, serviceIndex) in services"
            :key="serviceIndex"
            v-tooltip.top="{
              value: service?.title,
              class: 'region__service-tooltip',
            }"
            class="m-0 region__service"
          >
            <NuxtImg
              v-if="service?.icon?.src"
              :src="service?.icon?.src"
              :alt="service?.icon?.alt"
              :height="service?.icon?.height"
              :width="service?.icon?.width"
              class="region__service-image"
              loading="lazy"
            />
          </li>
        </ul>
      </template>
    </div>
  </div>
</template>
<style scoped lang="scss">
.region {
  $parent: &;
  background-color: $white;
  border-radius: 4px;
  cursor: pointer;
  min-height: 56px;
  height: 100%;
  padding: 6px 16px;
  transition: all 0.3s linear;
  position: relative;
  @include media-breakpoint-up(sm) {
    min-height: 80px;
    padding: 10px 24px;
  }
  @include media-breakpoint-up(xl) {
    @include responsive-spacing(border-radius, 4);
    @include responsive-spacing(min-height, 80);
    @include responsive-spacing(padding-left, 24);
    @include responsive-spacing(padding-right, 24);
    @include responsive-spacing(padding-top, 10);
    @include responsive-spacing(padding-bottom, 10);
  }
  &--witharrow {
    &:hover {
      @include media-breakpoint-up(md) {
        #{$parent} {
          &__title {
            &-value {
              color: $primary;
            }
          }
          &__arrow {
            &-icon {
              &:deep(path) {
                stroke: $primary;
              }
            }
          }
          &__bottom {
            opacity: 1;
            visibility: visible;
          }
        }
      }
    }
  }
  &__top {
    display: grid;
    gap: 16px;
    grid-template-columns: auto 16px;
    justify-content: space-between;
    height: 100%;
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 16);
      grid-template-columns: auto px-to-vw(16);
    }
  }
  &__title {
    align-items: center;
    display: flex;
    flex-direction: row;
    gap: 16px;
    transition: color 0.3s linear;
    &-value {
      word-break: break-word;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 16);
    }
  }
  &__quantity {
    align-items: center;
    background-color: $gray;
    border-radius: 50%;
    display: inline-flex;
    justify-content: center;
    min-height: 24px;
    min-width: 24px;
    padding: 2px 4px;
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(min-height, 24);
      @include responsive-spacing(min-width, 24);
      @include responsive-spacing(padding-top, 2);
      @include responsive-spacing(padding-bottom, 2);
      @include responsive-spacing(padding-left, 4);
      @include responsive-spacing(padding-right, 4);
    }
  }
  &__arrow {
    align-items: center;
    display: inline-flex;
    justify-content: center;
    &-icon {
      &:deep(path) {
        transition: stroke 0.3s linear;
      }
    }
  }
  &__bottom {
    background-color: $white;
    border-radius: 0 0 4px 4px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    left: 0;
    opacity: 0;
    padding: 11px 24px 24px;
    position: absolute;
    transition: all 0.3s linear;
    visibility: hidden;
    width: 100%;
    z-index: 2;
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(border-bottom-left-radius, 4);
      @include responsive-spacing(border-bottom-right-radius, 4);
      @include responsive-spacing(gap, 8);
      @include responsive-spacing(padding-top, 11);
      @include responsive-spacing(padding-bottom, 24);
      @include responsive-spacing(padding-left, 24);
      @include responsive-spacing(padding-right, 24);
    }
    &::before {
      box-shadow: 0px 4px 36px 0px #00000026;
      border-radius: 4px;
      bottom: 0;
      content: '';
      height: calc(100% + 70px);
      left: 0;
      position: absolute;
      width: 100%;
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(border-radius, 4);
        height: calc(100% + px-to-vw(70));
      }
    }
    &-title {
      color: $on-surface-variant-2;
    }
  }
  &__brands {
    column-gap: 16px;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    list-style: none;
    row-gap: 4px;
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(column-gap, 16);
      @include responsive-spacing(row-gap, 4);
    }
  }
  &__brand {
    width: 40px;
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(width, 40);
    }
    &-image {
      aspect-ratio: 1/1;
      height: auto;
      width: 100%;
    }
  }
  &__services {
    column-gap: 12px;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    list-style: none;
    row-gap: 4px;
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(column-gap, 12);
      @include responsive-spacing(row-gap, 4);
    }
  }
  &__service {
    position: relative;
    width: 40px;
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(width, 40);
    }
    &-image {
      aspect-ratio: 1/1;
      height: auto;
      width: 100%;
    }
  }
}
</style>
